import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import {
  type LexProduct,
  useUpdateProduct,
} from '@/shared/hooks/api/product'
import {
  editProductFormResolver,
  PRODUCT_FORM_VALIDATION_I18N_KEYS,
  type EditProductFormValues,
  type ProductFormErrorCode,
} from './Product.schema'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  useFormField,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'

function EditProductFormMessage() {
  const { error, formMessageId } = useFormField()
  const { t } = useTranslation('productCatalog')
  if (!error?.message) {
    return null
  }
  const code = error.message as ProductFormErrorCode
  const i18nKey = PRODUCT_FORM_VALIDATION_I18N_KEYS[code]
  const body = i18nKey != null ? t(i18nKey) : error.message
  return (
    <p
      data-slot='form-message'
      id={formMessageId}
      className='text-sm text-destructive'
    >
      {body}
    </p>
  )
}

function productToFormDefaults(product: LexProduct): EditProductFormValues {
  return {
    name: product.name ?? '',
    description: product.description ?? '',
    yearOfManufacture:
      product.yearOfManufacture != null
        ? String(product.yearOfManufacture)
        : '',
    productModel: product.productModel ?? '',
  }
}

type EditProductDialogProps = {
  product: LexProduct
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

export function EditProductDialog({
  product,
  open,
  onOpenChange,
  onSuccess,
}: EditProductDialogProps) {
  const { t } = useTranslation('productCatalog')
  const { mutateAsync: updateProduct, isPending } = useUpdateProduct()

  const form = useForm<EditProductFormValues>({
    resolver: editProductFormResolver,
    defaultValues: productToFormDefaults(product),
  })

  useEffect(() => {
    if (open) {
      form.reset(productToFormDefaults(product))
    }
  }, [open, product, form])

  async function onSubmit(values: EditProductFormValues) {
    try {
      const year = parseInt(values.yearOfManufacture, 10)
      await updateProduct({
        productId: product.id,
        name: values.name,
        description: values.description,
        yearOfManufacture: year,
        productModel:
          values.productModel.trim() === ''
            ? null
            : values.productModel.trim(),
      })
      onOpenChange(false)
      toast.success(t('detail.editDialog.saveSuccess'))
      onSuccess?.()
    } catch {
      toast.error(t('detail.editDialog.updateError'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[90vh] overflow-y-auto sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>{t('detail.editDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('detail.editDialog.description')}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
            <FormField
              control={form.control}
              name='name'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('dialog.fields.name')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('dialog.fields.namePlaceholder')}
                      autoComplete='off'
                      {...field}
                    />
                  </FormControl>
                  <EditProductFormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='description'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('dialog.fields.description')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('dialog.fields.descriptionPlaceholder')}
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <EditProductFormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='yearOfManufacture'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('dialog.fields.yearOfManufacture')}</FormLabel>
                  <FormControl>
                    <Input
                      inputMode='numeric'
                      pattern='[0-9]*'
                      placeholder={t('dialog.fields.yearPlaceholder')}
                      autoComplete='off'
                      {...field}
                    />
                  </FormControl>
                  <EditProductFormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='productModel'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('dialog.fields.productModel')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t(
                        'dialog.fields.productModelPlaceholder'
                      )}
                      autoComplete='off'
                      {...field}
                    />
                  </FormControl>
                  <EditProductFormMessage />
                </FormItem>
              )}
            />
            <DialogFooter className='gap-2 sm:gap-0'>
              <Button
                type='button'
                variant='outline'
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                {t('dialog.cancel')}
              </Button>
              <Button type='submit' disabled={isPending}>
                {isPending ? (
                  <>
                    <Loader2 className='me-2 size-4 animate-spin' />
                    {t('detail.editDialog.submitting')}
                  </>
                ) : (
                  t('detail.editDialog.submit')
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
